// ============================================
// Results Recovery
//
// Handles two return paths:
//   1. Direct link: /r/:sessionId — stores sessionId, redirects to /app
//   2. Magic link: /r/:sessionId?oobCode=...&mode=signIn — completes
//      Firebase passwordless sign-in, then stores sessionId and redirects.
//
// If magic link sign-in fails (expired, different device with no
// saved email), falls back to just restoring the sessionId so the
// resume modal can still offer to continue the journey.
// ============================================

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { handleMagicLinkReturn, getSavedMagicLinkEmail } from '../../services/auth/magicLinkService';
import { Mail, Loader2, AlertCircle } from 'lucide-react';

const STORAGE_KEY = 'hemologix_session_id';

export function ResultsRecovery() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    async function process() {
      // Store sessionId in localStorage
      if (sessionId) {
        try {
          localStorage.setItem(STORAGE_KEY, sessionId);
        } catch {
          // localStorage unavailable
        }
      }

      // Check if this is a magic link return
      const url = window.location.href;
      if (isSignInWithEmailLink(auth, url)) {
        const savedEmail = getSavedMagicLinkEmail();
        if (!savedEmail) {
          // Different device/browser — need to ask for email
          setNeedsEmail(true);
          setProcessing(false);
          return;
        }

        // Try to complete magic link sign-in
        const result = await handleMagicLinkReturn(sessionId || '');
        if (result) {
          navigate('/app', { replace: true });
          return;
        }
        // Fall through to redirect on failure
      }

      // Not a magic link, or magic link processing done — redirect
      navigate('/app', { replace: true });
    }

    process();
  }, [sessionId, navigate]);

  // Handle email submission for cross-device magic link
  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setProcessing(true);
    try {
      localStorage.setItem('hemologix_magic_link_email', trimmed);
      const result = await handleMagicLinkReturn(sessionId || '');
      if (result) {
        navigate('/app', { replace: true });
        return;
      }
      setError('This link may have expired. Please request a new one from the app.');
      setProcessing(false);
    } catch {
      setError('Sign-in failed. Please try again.');
      setProcessing(false);
    }
  }

  // Email prompt for cross-device magic link
  if (needsEmail) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-50 px-4">
        <div className="max-w-sm w-full">
          <div className="text-center mb-6">
            <h2 style={{ fontFamily: 'Instrument Serif, serif', fontSize: '20px', fontWeight: 700, color: '#18181B' }}>
              HEMOLOGIX<span style={{ color: '#E11D48' }}>.AI</span>
            </h2>
            <p className="text-zinc-500 text-[13px] mt-1">Welcome back</p>
          </div>

          <div className="bg-white border border-zinc-200 rounded p-6">
            <p className="text-[14px] font-medium text-zinc-900 mb-1">Confirm your email</p>
            <p className="text-zinc-500 text-[13px] leading-relaxed mb-4">
              Enter the email address you used when saving your results.
            </p>

            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  disabled={processing}
                  className="w-full pl-10 pr-3 py-2.5 rounded bg-zinc-50 border border-zinc-200 text-[14px]
                    text-zinc-900 placeholder:text-zinc-400 outline-none
                    focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 transition-colors
                    disabled:opacity-50"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-[13px] text-red-600 bg-red-50 px-3 py-2 rounded border border-red-100">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={processing || !email.trim()}
                className="w-full py-2.5 rounded text-white text-[14px] font-medium
                  hover:opacity-90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2"
                style={{ background: '#E11D48' }}
              >
                {processing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </form>

            <button
              onClick={() => navigate('/app', { replace: true })}
              className="w-full mt-3 py-2 text-zinc-400 text-[13px] hover:text-zinc-600 transition-colors"
            >
              Skip — start fresh instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="flex items-center justify-center h-screen bg-zinc-50">
      <div className="text-center">
        <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#E11D48' }} />
        <p className="text-zinc-500 text-[14px]">Loading your results...</p>
      </div>
    </div>
  );
}
